import { leadTimeCompositionSnapshotSchema } from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { z } from "zod";
import { LEAD_TIME_GRAINS, LEAD_TIME_SOURCES } from "@erp/shared-types";
import { LeadTimeOverrideRowSchema } from "./lead-time-overrides.contract";

// ─── Shared schemas ─────────────────────────────────────────────────────────

// Derived from the shared grain list so the contract tracks the backend's
// rung registry (adding a rung is one list edit on both sides).
export const LeadTimeGrainSchema = z.enum(LEAD_TIME_GRAINS);
export type LeadTimeGrainValue = z.infer<typeof LeadTimeGrainSchema>;

/** Where a resolved lead time came from: a measured rung, a manual
 * 'override', or 'none' when nothing answered. Same shared list the
 * forecast-run contract derives from. */
export const LeadTimeSourceSchema = z.enum(LEAD_TIME_SOURCES);
export type LeadTimeSourceValue = z.infer<typeof LeadTimeSourceSchema>;

/** One lead-time answer: the days, where they came from, and the sample
 * behind them (0 / null for an override or 'none'). */
export const LeadTimeResolutionSchema = z.object({
  leadTimeDays: z.number().nullable(),
  source: LeadTimeSourceSchema,
  sampleN: z.number(),
  sigmaDays: z.number().nullable(),
});
export type LeadTimeResolutionValue = z.infer<typeof LeadTimeResolutionSchema>;

/** One ladder rung as stored in im_lead_time_stats, scoped to the request's
 * (product, warehouse). `picked` marks the rung the resolver actually used. */
export const LeadTimeRungSchema = z.object({
  grain: LeadTimeGrainSchema,
  p50Days: z.number(),
  sigmaDays: z.number().nullable(),
  sampleN: z.number(),
  /** Whether this rung clears the minimum-receipts bar (see `minSamples`). */
  meetsMinSamples: z.boolean(),
  picked: z.boolean(),
});
export type LeadTimeRung = z.infer<typeof LeadTimeRungSchema>;

/**
 * One vendor's OWN declared lead time for this product × warehouse
 * (`ap_supplier_products.lead_time_days`, a verbatim copy of SKUSUPPARM
 * GILEAD). Deliberately NOT a ladder rung and never part of `resolved`:
 * forecasting and phase-1 need detection ignore it and use `fallbackDays`
 * instead, because the declared value is AS400's own thin last-6-receipts
 * calc. Phase 2 does fall back to it for a buy leg's displayed arrival
 * estimate, which is why an unmeasured series reads "no estimate" on the
 * inventory page and "~6d" on a suggestion. Reported so both surfaces can
 * say where each number came from.
 */
export const DeclaredLeadTimeSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  leadTimeDays: z.number(),
  /** False = an org-wide vendor row (`warehouse_id IS NULL`), which phase 2
   * offers at every warehouse; true = a row scoped to this one. */
  warehouseScoped: z.boolean(),
});
export type DeclaredLeadTime = z.infer<typeof DeclaredLeadTimeSchema>;

// ─── Get detail (one product × warehouse) ───────────────────────────────────

export const GetLeadTimeDetailInputSchema = z.object({
  warehouseId: z.string().uuid(),
  /** `im_products_v3.key`: the SKU, and the only product identity procurement has. */
  productV3Key: z.string().min(1),
});

export const GetLeadTimeDetailResponseSchema = z.object({
  currentComposition: leadTimeCompositionSnapshotSchema.optional(),
  policyComposition: leadTimeCompositionSnapshotSchema.nullable().optional(),
  changedSincePolicyRun: z.boolean().optional(),
  /** Ladder order: item_loc → item → item_national. Rungs with no stats row
   * for this (product, warehouse) are simply absent. */
  rungs: z.array(LeadTimeRungSchema),
  /** What the engines/forecasting use: the active manual override when one
   * applies (source 'override'), else the ladder's answer. Source 'none' = no
   * rung answered; leadTimeDays is null and surfaces show "no estimate" (with
   * `fallbackDays` as what the engines assume). */
  resolved: LeadTimeResolutionSchema,
  /** The ladder's own answer with overrides ignored: the calculated value to
   * show beside a pin. Equals `resolved` when no override applies. */
  observed: LeadTimeResolutionSchema,
  /** The override row `resolved` came from; null when the ladder answered. */
  override: LeadTimeOverrideRowSchema.nullable(),
  /** Vendors' declared lead times for this product × warehouse, shortest
   * first. Empty when no supplier row carries one. Descriptive only: see
   * {@link DeclaredLeadTimeSchema} for why it never enters `resolved`. */
  declared: z.array(DeclaredLeadTimeSchema),
  /** Flat days the engines assume when unmeasured (DEFAULT_LEAD_TIME_DAYS;
   * destined for org settings). Surface as "engine assumes ~N days". */
  fallbackDays: z.number(),
  /** Minimum closed receipts a rung needs before it is trusted. */
  minSamples: z.number(),
  /** Division the warehouse belongs to; null = no division mapping, in which
   * case the ladder cannot apply and `resolved` is the flat fallback. */
  divisionId: z.string().uuid().nullable(),
  /** Receipt-window start / refresh stamps of the underlying stats rows. */
  receiptsSince: z.string().nullable(),
  computedAt: z.string().nullable(),
});

export type GetLeadTimeDetailInput = z.infer<
  typeof GetLeadTimeDetailInputSchema
>;
export type GetLeadTimeDetailResponse = z.infer<
  typeof GetLeadTimeDetailResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const leadTimesContracts = {
  getDetail: oc
    .input(GetLeadTimeDetailInputSchema)
    .output(GetLeadTimeDetailResponseSchema)
    .route({
      method: "GET",
      summary: "Explain a SKU's observed lead time",
      description:
        "For a SKU at a warehouse, shows each lead-time estimate level with median, spread and sample count, which one was chosen, and the flat fallback.",
    }),
} as const;
